import { useState, useEffect } from "react";
import { getCrmData, formatNumber } from "../Utils";

/**
 * Custom hook for managing incoming call data fetching and display logic
 *
 * @param {Object} incomingData - The incoming call data object containing number and info
 * @param {string} uniqueid - Unique identifier for the incoming call
 * @returns {Object} - Contains crmData, loading, error states and helper functions
 */
export const useIncomingCallData = (incomingData, uniqueid) => {
  const [crmData, setCrmData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const callKey = String(
    uniqueid ||
      incomingData?.info?.uniqueid ||
      incomingData?.timestamp ||
      incomingData?.number ||
      ""
  );

  // Fetch CRM data when the current call changes. The guard avoids an older
  // request updating the UI after a new call has already arrived.
  useEffect(() => {
    let active = true;

    const safeSetCrmData = (data) => {
      if (active) {
        setCrmData(data || null);
      }
    };

    const fetchCrmData = async () => {
      setCrmData(null);
      setError(null);

      if (!incomingData?.number) {
        setLoading(false);
        return;
      }

      if (incomingData?.info?.internalCall === true) {
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const contactCache = window.__telephonyCache?.contacts;
        const cacheKey = uniqueid || incomingData?.info?.uniqueid;
        const telephonyCache = cacheKey && contactCache ? contactCache.get(cacheKey) : null;

        if (telephonyCache && Object.prototype.hasOwnProperty.call(telephonyCache, 'contact')) {
          console.log('Using cached CRM data for uniqueid:', cacheKey);
          safeSetCrmData(telephonyCache.contact || null);
          return;
        }

        console.log('Fetching CRM data for number:', incomingData.number);
        await getCrmData(incomingData.number, safeSetCrmData);
      } catch (err) {
        if (!active) return;
        console.error('Error fetching CRM data:', err);
        setError('Não foi possível carregar informações do contato');
        setCrmData(null);
        // Continue showing dialog even if CRM data fails
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchCrmData();
    return () => {
      active = false;
    };
  }, [callKey, incomingData?.number, incomingData?.info?.internalCall, uniqueid]);

  /**
   * Get the display name for the incoming call
   * Priority: CRM name > formatted phone number > fallback text
   */
  const getDisplayName = () => {
    try {
      if (crmData?.name) return crmData.name;
      if (incomingData?.number) return formatNumber(incomingData);
      return "Chamada Recebida";
    } catch (err) {
      console.error('Error getting display name:', err);
      return "Chamada Recebida";
    }
  };

  /**
   * Get initials from a name (first 2 words, first letter each)
   */
  const getInitials = (name) => {
    try {
      if (!name) return "?";
      return name
        .split(" ")
        .map((word) => word.charAt(0))
        .slice(0, 2)
        .join("")
        .toUpperCase();
    } catch (err) {
      console.error('Error getting initials:', err);
      return "?";
    }
  };

  return {
    crmData,
    loading,
    error,
    getDisplayName,
    getInitials,
  };
};
